import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import cn from 'classnames';

import AudioChatItem from '@app/components/audio-build/audio-chat/audio-chat-item';
import AudioChatEnd from '@app/components/audio-build/audio-chat/audio-chat-end';
import useTranslation from '@app/hooks/use-translation';
import { useAudioContext } from '@app/components/audio-build/record-provider';
import connector, { PropsFromRedux } from '@app/utils/store';
import { CircleButton, DropMenu, Toggle, TooltipWrapper } from '@ui';
import { CalculatedRecordMetric, Dialog } from '@app/interfaces/records';
import AudioChatLoadingItem from '@app/components/audio-build/audio-chat/audio-chat-loading-item';

import activeMetricBuilder from '@app/components/audio-build/audio-chat/active-text-builder';

import { CHAT_VIEW } from '@app/utils/constants';

import { useGetRecordTranscriptionModelsQuery } from '@app/store/api/audio-record.api';

import { DropMenuItem } from '@app/components/ui/drop-menu/drop-menu.type';

import { useLazyDownloadRecordChatTranscriptionQuery } from '@app/store/api/records.api';
import { useLazyDownloadChatRecordTranscriptionQuery } from '@app/store/api/chat-api/all-chats.api';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import { chartWrapperClassName, chatContainerClassName } from './audio-chat.styles';

type AudioChatProps = {
  transcription?: Array<Dialog>;
  activeMetric?: CalculatedRecordMetric[string];
  onChangeActiveChartTiming: React.Dispatch<
    React.SetStateAction<{ from: number; to: number } | undefined>
  >;
  activeMessageId: Array<string> | null;
  currentAsrModel?: string;
  audioBoolean?: boolean;
  currentTemplate: 'chat' | 'voice';
  handleUpdateRecordByAsrModel?(asrModel: string): void;
} & PropsFromRedux;

const AudioChat: FC<AudioChatProps> = (props) => {
  const {
    onChangeActiveChartTiming,
    transcription,
    activeMessageId,
    activeMetric,
    currentAsrModel,
    audioBoolean,
    currentTemplate,
    handleUpdateRecordByAsrModel,
  } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: userInfo } = useGetUserInfoQuery();

  const { recordId, toggleEmotions } = useAudioContext();
  const { data: recordModels, isLoading } = useGetRecordTranscriptionModelsQuery(
    { record_id: recordId || '' },
    { skip: currentTemplate === 'chat' || !recordId },
  );
  const { t } = useTranslation('components.audio');
  const [autoScroll, setAutoScroll] = useState(true);
  const [isDialogType, toggleDialogType] = useState<boolean>(
    Boolean(Number(localStorage.getItem(CHAT_VIEW) || 0)),
  );

  useEffect(() => {
    if (activeMessageId?.length) {
      const activeDiv: HTMLDivElement | null = document?.querySelector(
        `#${String(activeMessageId[0])}`,
      );

      if (activeDiv && scrollRef.current && autoScroll) {
        scrollRef.current.scrollTo({ top: activeDiv.offsetTop, behavior: 'smooth' });
      }
    }
  }, [activeMessageId, autoScroll]);

  const iconButtonClassName = cn('text-defaultColor');
  const recordModalsOptionList: DropMenuItem[] =
    useMemo(
      () =>
        recordModels?.transcriptions_list.map((transcriptionModel) => ({
          key: transcriptionModel.asr_model_id,
          title: transcriptionModel.asr_model_name,
          isActive: (currentAsrModel || '') === transcriptionModel.asr_model_id,
        })),
      [currentAsrModel, recordModels?.transcriptions_list],
    ) || [];
  function handleChatClickTimeEvent({ from, to }: { from: number; to: number }) {
    onChangeActiveChartTiming({ from, to });
  }

  const getAsrModelNameById = recordModels?.transcriptions_list.find(
    (model) => model.asr_model_id === currentAsrModel,
  )?.asr_model_name;

  const renderMessage = () => {
    let lastActiveDistance = 0;
    if (transcription && !isLoading) {
      return (
        <>
          {transcription.map((dialogItem, index) => {
            const { phrase, lastDistance } = activeMetricBuilder({
              dialog: dialogItem,
              activeMetricSetup: activeMetric || null,
              lastDistance: { distance: lastActiveDistance, owner: dialogItem.channel_id },
            });
            lastActiveDistance = lastDistance.coloredCountIterator;
            return (
              <AudioChatItem
                key={index}
                id={dialogItem.id}
                isDialogType={isDialogType}
                active={activeMessageId?.includes(dialogItem.id) || false}
                phrase={phrase}
                emotion={dialogItem.emotion}
                toggleEmotions={toggleEmotions}
                duration={{ from: dialogItem.from, to: dialogItem.to }}
                channelId={dialogItem.channel_id}
                channelConfig={dialogItem.channel_config}
                isChat={currentTemplate === 'chat'}
                onClick={() =>
                  handleChatClickTimeEvent({ from: dialogItem.from, to: dialogItem.to || 5000 })
                }
              />
            );
          })}
        </>
      );
    } else
      return (
        <div>
          <AudioChatLoadingItem count={4} />
        </div>
      );
  };
  const [downloadTranscriptionForVoice] = useLazyDownloadRecordChatTranscriptionQuery();
  const [downloadTranscriptionForChats] = useLazyDownloadChatRecordTranscriptionQuery();

  const downloadChatTranscription = (id: string, format: 'TXT' | 'CSV') => {
    if (currentTemplate === 'chat') {
      return downloadTranscriptionForChats({
        chat_id: id,
        format,
      });
    } else {
      return downloadTranscriptionForVoice({
        record_id: id,
        format,
        asr_model_id: currentAsrModel,
      });
    }
  };
  //  { chat: getChatFileChatRecordRequest, voice: getFileChatRecordRequest };
  function handleDownloadTranscription(format: string) {
    try {
      downloadChatTranscription(recordId, format as 'TXT' | 'CSV');
    } catch {
      toast.error(t('translation_download_false'));
    }
  }

  function toggleDialogTypeHandler() {
    toggleDialogType((prevState) => !prevState);
    localStorage.setItem(CHAT_VIEW, String(Number(!isDialogType)));
  }

  return (
    <div className={chartWrapperClassName}>
      <div className={chatContainerClassName}>
        <div>
          <div className="text-0color text-lg font-bold ">{t('dialog')}</div>
          {(process.env.REACT_APP_THEME !== 'rtk' || currentTemplate === 'voice') && (
            <div>
              <DropMenu
                dropSize={210}
                onClick={(key) => handleUpdateRecordByAsrModel?.(key)}
                menu={recordModalsOptionList}
                position="start"
              >
                <div className="noDrag text-4color font-[400] hover:underline leading-[10px] tracking-tight cursor-pointer text-[12px]">
                  {t('recog_model')} &nbsp;
                  {getAsrModelNameById}
                </div>
              </DropMenu>
            </div>
          )}
        </div>
        <div className="inline-flex items-center">
          {/*<CircleButton*/}
          {/*  icon="ZoomIcon"*/}
          {/*  size={16}*/}
          {/*  className={classNames(iconButtonClassName, 'mr-[19px]')}*/}
          {/*/>*/}
          <div className="flex items-center gap-[5px] text-[12px] text-3color pr-[3px]">
            <span className="mt-[-3px]">{t('auto_scroll')}</span>
            <Toggle checked={autoScroll} onChecked={(val) => setAutoScroll(val)} />
          </div>
          <TooltipWrapper content={t('chat_type')} id="change_chat_view_button">
            <CircleButton
              icon="SortIcon"
              size={17}
              className={iconButtonClassName}
              onClick={toggleDialogTypeHandler}
            />
          </TooltipWrapper>
          <DropMenu
            dropSize={70}
            onClick={handleDownloadTranscription}
            disable={userInfo?.qm_role === 'operator'}
            menu={[
              { title: 'TXT', key: 'TXT' },
              { title: 'CSV', key: 'CSV' },
            ]}
          >
            <CircleButton
              hintTitle={t('popup_hints.download_transcript')}
              icon="DownloadCloudIcon"
              disable={userInfo?.qm_role === 'operator'}
              size={19}
              className={iconButtonClassName}
            />
          </DropMenu>
        </div>
      </div>
      <div className="h-[calc(100%-5.5rem)] overflow-hidden">
        <div
          ref={scrollRef}
          className={cn(
            !isDialogType && 'pl-[70px] ml-[30px]',
            'px-[27px] h-full overflow-y-auto overflow-hidden',
          )}
        >
          <ol
            className={cn(
              !isDialogType && 'ml-[180px] border-l px-[27px] border-gray-200',
              'relative block  p-4',
            )}
          >
            {renderMessage()}
            {transcription && (
              <AudioChatEnd audioBoolean={audioBoolean} isEmpty={!transcription?.length} />
            )}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default connector(AudioChat);
